// Front/src/pages/NewScaleType.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../style/NewScale.css';
import { useAuthContext } from "../hooks/useAuthContext";
import { toast, ToastContainer } from 'react-toastify';
import { Helmet } from "react-helmet-async";
import { useTranslate } from '../hooks/useTranslate';
import { useLanguageContext } from '../context/LanguageContext';
import { upload_scale_type } from "../api";
import { generateScaleTypeJson } from "../utils/generateScaleTypeJson";
import { validateScaleTypeData, isPositiveNumber } from "../utils/scaleTypeFormValidation"; // for validations and red borders
import { getRankAndMinFromNandD } from "../utils/mathHelper"; // for calculating rank and Min
import DivisionTable from './DivisionTable'; // new division table

const NewScaleType = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location?.state || JSON.parse(localStorage.getItem("newScaleTypeNavState") || "{}");
    const t = useTranslate();
    const { language } = useLanguageContext();
    const { user } = useAuthContext();
    const userId = user?.id || null;
    const [isUploading, setIsUploading] = useState(false);
    const [touchedFields, setTouchedFields] = useState({}); // to know if fields are empty
    const [showDivisionTable, setShowDivisionTable] = useState(false);

    useEffect(() => {
        if (location.state) {
            localStorage.setItem("newScaleTypeNavState", JSON.stringify(location.state));
        }
    }, [location.state]);    

    const requiredFields = [
        // Scale auto-filled details
        "manufacturer",
        "model",
        "accuracyClass",
        "classType",
        "dValue",
        "eValue",
        "maxValue",
        "minValue",
        "nValue",
    ];

    const saved = localStorage.getItem("newScaleTypeFormData");

    const [formData, setFormData] = useState(() => {
      const defaultData = {
        catalogNumber: navState?.catalogNumber || '',
        standard: 'OIML',
        manufacturer: '',
        model: '',
        weightUnits: 0,
        maxValue: '',
        minValue: '',
        eValue: '',
        dValue: '',
        nValue: '',
        classType: '',
        notes: '',
      };
    
      return saved ? { ...defaultData, ...JSON.parse(saved) } : defaultData;
    });    

    // use the stored input from the user.
    useEffect(() => {
        localStorage.setItem("newScaleTypeFormData", JSON.stringify(formData));
    }, [formData]);
      

    const handleChange = (e) => {
        const { name, value } = e.target;

        setTouchedFields(prev => ({ ...prev, [name]: true }));

        setFormData(prevData => {
            let updatedData = {
                ...prevData,
                [name]: name === "catalogNumber" ? value.toUpperCase() : value
            };
    
            // Auto-calculate nValue when maxValue or eValue changes
            if (name === "maxValue" || name === "eValue") {
                const max = parseFloat(name === "maxValue" ? value : prevData.maxValue);
                const e = parseFloat(name === "eValue" ? value : prevData.eValue);
    
                if (!isNaN(max) && !isNaN(e) && e !== 0) {
                    updatedData.nValue = Math.round(max / e).toString();
                } else {
                    updatedData.nValue = "";
                }
            }
    
            return updatedData;
        });
    };
    
    const getFieldClass = (field) => {
        const val = formData[field];
        if (!touchedFields[field]) return "";
    
        // Mark empty required fields
        if (requiredFields.includes(field) && (val === "" || val === null || val === undefined || val.toString().trim() === "")) {
            return "invalid-input";
        }
    
        // Numeric validations
        if (["maxValue", "minValue", "eValue", "dValue", "nValue"].includes(field) && !isPositiveNumber(val)) {
            return "invalid-input";
        }
    
        // e < d validation
        if (field === "eValue" && parseFloat(formData.eValue) < parseFloat(formData.dValue)) {
            return "invalid-input";
        }
    
        return "";
    };    
    
    // for debugging
    // const PostError = (message) => {
    //     toast.error(message, {
    //         icon: false,
    //         autoClose: 3000,
    //     });
    //     console.error(message);
    // };

    // effect to calculate rank and accuracy class
    useEffect(() => {
        const n = parseFloat(formData.nValue);
        const d = parseFloat(formData.dValue);
    
        if (!isNaN(n) && !isNaN(d) && n > 0 && d > 0) {
            const result = getRankAndMinFromNandD(n, d);
            if (result) {
                setFormData(prev => ({
                    ...prev,
                    accuracyClass: result.accuracyClass,
                    minValue: prev.minValue?.toString().trim() === ""
                        ? result.min.toString()
                        : prev.minValue
                }));
            }
        }
    }, [formData.nValue, formData.dValue]);    

    const handleSubmit = async (e) => {

        e.preventDefault();

        const errorMessage = validateScaleTypeData(formData, t);
        if (errorMessage) {
            toast.error(errorMessage, {
                icon: false,
                autoClose: 3000,
            });
    
            // Mark all fields as touched to show errors
            const allTouched = Object.keys(formData).reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});
            setTouchedFields(allTouched);
    
            return;
        }    

        // Reset any previous messages.
        setIsUploading(true);

        try {
            const scaleTypeJson = generateScaleTypeJson(formData, userId); // generate the proper JSON
            const uploadResponse = await upload_scale_type(scaleTypeJson); // send the generated object
    
            if (uploadResponse.status === 200) {
                localStorage.removeItem("newScaleTypeFormData"); // clear stored data.
                toast.success(t.scaleTypeCreatedSuccessfully || "✅ Scale Type Created Successfully!", {
                    onClose: () =>
                        navigate('/newscale', {
                          state: {
                            ...navState,
                            catalogNumber: formData.catalogNumber,
                          },
                        }),                      
                    autoClose: 3000,
                    icon: false,
                });
            } else {
                toast.error(t.scaleTypeUploadError || "Scale Type Upload Failed");
            }
        } catch (error) {
            toast.error((t.scaleTypeUploadError || "Scale Type Upload Failed") + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="new-scale-container" data-theme={document.body.classList.contains('dark') ? 'dark' : 'light'} dir={language === 'he' ? 'rtl' : 'ltr'}>
            <Helmet>
                <title>Shekel Technet - {t.addScaleType || "Add Scale Type"}</title>
            </Helmet>

            <button
                type="button"
                className="button-go-back"
                onClick={() => {
                    localStorage.removeItem("newScaleTypeFormData"); // Clear stored data
                    navigate('/newscale', { state: { ...navState } });
                }}
            >
                {t.goBack}
            </button>

            <h2>{t.addScaleType || "Add Scale Type"}</h2>
            <form onSubmit={handleSubmit}>

                <div className="form-group">
                    <label>{t.catalogNumber}</label>
                    <input
                        type="text"
                        name="catalogNumber"
                        value={formData.catalogNumber}
                        onChange={handleChange}
                        className={`numeric-input ${getFieldClass("catalogNumber")}`}
                    />
                </div>

                <div className="form-group">
                    <label>{t.standard}</label>
                    <select name="standard" value={formData.standard} onChange={handleChange}>
                        <option value="OIML">OIML</option>
                        <option value="NTEP">NTEP</option>
                        <option value="NONE">{t.none || "None"}</option>
                    </select>
                </div>


                <label>{t.manufacturer}</label>
                <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("manufacturer")}`}
                />

                <label>{t.model}</label>
                <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("model")}`}
                />

                <label>{t.weightUnit}</label>
                <select name="weightUnits" value={formData.weightUnits} onChange={handleChange} >
                    <option value="0">{t.miligram}</option>
                    <option value="1">{t.gram}</option>
                    <option value="2">{t.kilogram}</option>
                    <option value="3">{t.lb}</option>
                </select>

                <label>{t.maxValue}</label>
                <input
                    type="number"
                    name="maxValue"
                    value={formData.maxValue}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("maxValue")}`}
                />

                <button 
                    type="button" 
                    onClick={() => setShowDivisionTable(prev => !prev)}
                    className="toggle-division-table-button"
                >
                    {showDivisionTable ? t.hideDivisionTable : t.showDivisionTable}
                </button>

                {showDivisionTable && <DivisionTable />}

                <label>{t.minValue}</label>
                <input
                    type="number"
                    name="minValue"
                    value={formData.minValue}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("minValue")}`}
                />

                <label>{t.eValue}</label>
                <input
                    type="number"
                    name="eValue"
                    value={formData.eValue}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("eValue")}`}
                />

                <label>{t.dValue}</label>
                <input
                    type="number"
                    name="dValue"
                    value={formData.dValue}
                    onChange={handleChange}
                    className={`numeric-input ${getFieldClass("dValue")}`}
                />

                <label>{t.nValue}</label>
                <input
                    type="number"
                    name="nValue"
                    value={formData.nValue}
                    readOnly
                    className={`numeric-input ${getFieldClass("nValue")}`}
                />

                <label>{t.accuracyClass}</label>
                <select
                    name="accuracyClass" className={getFieldClass("accuracyClass")}
                    value={
                        formData.accuracyClass === 1 ? "One" :
                        formData.accuracyClass === 2 ? "Two" :
                        formData.accuracyClass === 3 ? "Three" :
                        formData.accuracyClass === 4 ? "Four" : ""
                    }
                    onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        accuracyClass:
                            e.target.value === "One" ? 1 :
                            e.target.value === "Two" ? 2 :
                            e.target.value === "Three" ? 3 :
                            e.target.value === "Four" ? 4 : ""
                    }))}
                >
                    <option value="">{t.selectAccuracyClass}</option>
                    <option value="One">{t.one}</option>
                    <option value="Two">{t.two}</option>
                    <option value="Three">{t.three}</option>
                    <option value="Four">{t.four}</option>
                </select>

                <label>{t.generalComments}</label>
                <input
                    type="text"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t.enterComments}
                />

                <div className="button-container">
                    <button type="submit" disabled={isUploading}>
                        {isUploading ? t.submitButtonUploading || "Uploading..." : t.submitScaleType || "Submit Scale Type"}
                    </button>
                </div>

            </form>

            <ToastContainer position="top-center" autoClose={3000} toastClassName="custom-toast" closeButton={true} />
        </div>
    );
};

export default NewScaleType;

